import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AdminUser {
  user_id: string;
  email: string | null;
  full_name: string | null;
  specialty: string | null;
  status: string;
  plan: string | null;
  reports_used: number;
  tokens_used: number;
  trial_expires_at: string;
  created_at: string;
  role: string | null;
}

interface UseAdminUsersResult {
  users: AdminUser[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  fetchUsers: (statusFilter?: string, searchTerm?: string, page?: number) => Promise<void>;
  activateUser: (userId: string, role?: string, reason?: string) => Promise<boolean>;
  deactivateUser: (userId: string, reason?: string) => Promise<boolean>;
}

export function useAdminUsers(): UseAdminUsersResult {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback(async (
    statusFilter?: string,
    searchTerm?: string,
    pageNum: number = 1
  ) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_list_users', {
        p_status_filter: statusFilter || null,
        p_search_term: searchTerm || null,
        p_page: pageNum,
        p_page_size: pageSize
      });

      if (error) {
        // If RPC doesn't exist, fall back to direct table queries
        if (error.code === '42883' || error.code === 'PGRST202' || (error as { status?: number }).status === 404) {
          console.warn('admin_list_users RPC not found - falling back to direct table queries');

          // Get all accounts first (this is the source of truth for user status)
          let accountsData: Array<{ user_id: string; status: string; plan?: string | null; reports_used?: number; tokens_used?: number; trial_expires_at: string | null; created_at: string }> | null = null;
          let count: number | null = null;

          // Try extended columns first
          let accountsQuery = supabase
            .from('accounts')
            .select('user_id, status, plan, reports_used, tokens_used, trial_expires_at, created_at', { count: 'exact' })
            .order('created_at', { ascending: false });

          if (statusFilter) {
            accountsQuery = accountsQuery.eq('status', statusFilter);
          }

          const extendedResult = await accountsQuery;

          if (extendedResult.error) {
            // Fall back to basic columns
            console.warn('Extended accounts query failed, falling back to basic columns:', extendedResult.error.message);

            let basicQuery = supabase
              .from('accounts')
              .select('user_id, status, trial_expires_at, created_at', { count: 'exact' })
              .order('created_at', { ascending: false });

            if (statusFilter) {
              basicQuery = basicQuery.eq('status', statusFilter);
            }

            const basicResult = await basicQuery;
            if (basicResult.error) {
              if (basicResult.error.code === '42P01' || basicResult.error.message?.includes('does not exist') || basicResult.error.code === 'PGRST204') {
                console.warn('accounts table not found - returning empty array');
                setUsers([]);
                setTotal(0);
                setPage(pageNum);
                return;
              }
              throw basicResult.error;
            }

            accountsData = basicResult.data;
            count = basicResult.count;
          } else {
            accountsData = extendedResult.data;
            count = extendedResult.count;
          }

          // Get all user IDs from accounts
          const accountUserIds = (accountsData || []).map(a => a.user_id);

          // Get profiles for these users
          let profilesMap = new Map<string, { id: string; email: string | null; full_name: string | null; created_at: string }>();

          if (accountUserIds.length > 0) {
            const { data: profilesData } = await supabase
              .from('profiles')
              .select('id, email, full_name, created_at')
              .in('id', accountUserIds);

            if (profilesData) {
              profilesMap = new Map(profilesData.map(p => [p.id, p]));
            }
          }

          // Get user roles
          let rolesMap: Record<string, string> = {};

          if (accountUserIds.length > 0) {
            try {
              const { data: rolesData } = await supabase
                .from('user_roles')
                .select('user_id, role')
                .in('user_id', accountUserIds);

              if (rolesData) {
                rolesMap = rolesData.reduce((acc, r) => {
                  acc[r.user_id] = r.role;
                  return acc;
                }, {} as Record<string, string>);
              }
            } catch {
              console.warn('user_roles table not accessible');
            }
          }

          // Filter by search term if provided
          let filteredAccounts = accountsData || [];
          if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filteredAccounts = filteredAccounts.filter((acc) => {
              const profile = profilesMap.get(acc.user_id);
              return (
                (profile?.full_name?.toLowerCase().includes(searchLower)) ||
                (profile?.email?.toLowerCase().includes(searchLower)) ||
                (acc.user_id.toLowerCase().includes(searchLower))
              );
            });
          }

          // Apply pagination after filtering
          const totalFiltered = filteredAccounts.length;
          const paginatedAccounts = filteredAccounts.slice(
            (pageNum - 1) * pageSize,
            pageNum * pageSize
          );

          // Map data
          const mappedUsers = paginatedAccounts.map((acc) => {
            const profile = profilesMap.get(acc.user_id);
            return {
              user_id: acc.user_id,
              email: profile?.email || null,
              full_name: profile?.full_name || null,
              specialty: null,
              status: acc.status,
              plan: acc.plan || null,
              reports_used: acc.reports_used ?? 0,
              tokens_used: acc.tokens_used ?? 0,
              trial_expires_at: acc.trial_expires_at || '',
              created_at: acc.created_at,
              role: rolesMap[acc.user_id] || null
            };
          }) as AdminUser[];

          setUsers(mappedUsers);
          setTotal(searchTerm ? totalFiltered : (count || 0));
          setPage(pageNum);
          return;
        }
        throw error;
      }

      const result = data as unknown as { success: boolean; users: AdminUser[]; total: number; page: number; error?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch users');
      }

      setUsers(result.users || []);
      setTotal(result.total || 0);
      setPage(result.page || 1);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const activateUser = useCallback(async (
    userId: string,
    role: 'admin' | 'radiologist' = 'radiologist',
    reason?: string
  ): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('admin_activate_user', {
        p_target_user_id: userId,
        p_assigned_role: role,
        p_reason: reason || null
      });

      if (error) {
        // If RPC doesn't exist, fall back to direct table update
        if (error.code === '42883' || error.code === 'PGRST202' || (error as { status?: number }).status === 404) {
          console.warn('admin_activate_user RPC not found - falling back to direct table update');

          // Update account status to active
          const { error: updateError } = await supabase
            .from('accounts')
            .update({ status: 'active' })
            .eq('user_id', userId);

          if (updateError) throw updateError;

          // Try to set user role (might fail if user_roles doesn't exist)
          try {
            // First try to update existing role
            const { data: existingRole } = await supabase
              .from('user_roles')
              .select('user_id')
              .eq('user_id', userId)
              .single();

            if (existingRole) {
              await supabase
                .from('user_roles')
                .update({ role })
                .eq('user_id', userId);
            } else {
              await supabase
                .from('user_roles')
                .insert({ user_id: userId, role });
            }
          } catch {
            console.warn('Could not set user role - user_roles table may not be accessible');
          }

          // Log to audit_logs if possible
          try {
            await supabase.from('audit_logs').insert({
              admin_id: (await supabase.auth.getUser()).data.user?.id,
              action_type: 'user_activated',
              target_type: 'user',
              target_id: userId,
              new_state: { status: 'active', role },
              reason: reason || 'Activated via admin panel'
            });
          } catch {
            console.warn('Could not log activation to audit_logs');
          }

          toast.success('User activated successfully');
          return true;
        }
        throw error;
      }

      const result = data as unknown as { success: boolean; message?: string; error?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to activate user');
      }

      toast.success(result.message || 'User activated successfully');
      return true;
    } catch (err) {
      console.error('Failed to activate user:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to activate user');
      return false;
    }
  }, []);

  const deactivateUser = useCallback(async (
    userId: string,
    reason?: string
  ): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('admin_deactivate_user', {
        p_target_user_id: userId,
        p_reason: reason || null
      });

      if (error) {
        // If RPC doesn't exist, fall back to direct table update
        if (error.code === '42883' || error.code === 'PGRST202' || (error as { status?: number }).status === 404) {
          console.warn('admin_deactivate_user RPC not found - falling back to direct table update');

          // Update account status to suspended
          const { error: updateError } = await supabase
            .from('accounts')
            .update({ status: 'suspended' })
            .eq('user_id', userId);

          if (updateError) throw updateError;

          // Log to audit_logs if possible
          try {
            await supabase.from('audit_logs').insert({
              admin_id: (await supabase.auth.getUser()).data.user?.id,
              action_type: 'user_deactivated',
              target_type: 'user',
              target_id: userId,
              new_state: { status: 'suspended' },
              reason: reason || 'Deactivated via admin panel'
            });
          } catch {
            console.warn('Could not log deactivation to audit_logs');
          }

          toast.success('User deactivated successfully');
          return true;
        }
        throw error;
      }

      const result = data as unknown as { success: boolean; message?: string; error?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to deactivate user');
      }

      toast.success(result.message || 'User deactivated successfully');
      return true;
    } catch (err) {
      console.error('Failed to deactivate user:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to deactivate user');
      return false;
    }
  }, []);

  return {
    users,
    total,
    page,
    pageSize,
    loading,
    fetchUsers,
    activateUser,
    deactivateUser
  };
}
